import type { LibrarySourceType } from "@/lib/library";

/**
 * Per-source-type row tones for the source picker (SourceCombobox).
 * Returns CSS color strings driven by HSL+alpha tokens defined in src/index.css
 * (light/dark handled there). Backgrounds (`idle`/`hover`/`selected`) are
 * tinted; foregrounds (`title`/`meta`) are the same hue family at higher
 * contrast so text reads as a coordinated wash on top of the row tint.
 */
type ToneKey = "book" | "article" | "chapter" | "paper" | "website" | "report" | "podcast" | "other";

const TONE_BY_SOURCE: Record<NonNullable<LibrarySourceType>, ToneKey> = {
  book: "book",
  journal_article: "article",
  chapter: "chapter",
  proceedings_article: "paper",
  website: "website",
  report: "report",
  podcast: "podcast",
  other: "other",
};

export type SourceRowToneVars = {
  idle: string;
  hover: string;
  selected: string;
  title: string;
  meta: string;
  /** Saturated rail accent — used for the colored left edge on list cards. */
  accent: string;
};

export const getSourceRowToneVars = (
  type: LibrarySourceType | null | undefined,
): SourceRowToneVars => {
  const tone = (type && TONE_BY_SOURCE[type]) || "other";
  return {
    idle: `hsl(var(--row-source-${tone}-idle))`,
    hover: `hsl(var(--row-source-${tone}-hover))`,
    selected: `hsl(var(--row-source-${tone}-selected))`,
    title: `hsl(var(--row-source-${tone}-title))`,
    meta: `hsl(var(--row-source-${tone}-meta))`,
    accent: `hsl(var(--row-source-${tone}-accent))`,
  };
};

/**
 * Map a Readwise source-token key (book/article/podcast/tweet/pdf/email/
 * video/supplemental/unknown) onto a LibrarySourceType so Readwise rows can
 * share the same tone palette as My Library / My Excerpts.
 */
export const readwiseTokenToLibraryType = (
  key: string | null | undefined,
): LibrarySourceType => {
  switch (key) {
    case "book": return "book";
    case "article": return "journal_article";
    case "pdf": return "report";
    case "podcast": return "podcast";
    case "video":
    case "tweet":
    case "email":
      return "website";
    case "supplemental":
      return "other";
    default:
      return "other";
  }
};
